import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

interface CellCorrection {
  id?: string
  operation?:
    | 'cell'
    | 'insert_row'
    | 'delete_row'
    | 'insert_column'
    | 'delete_column'
    | 'rename_column'
  hole_id: string
  extractionId?: string
  dataType: string
  rowIndex: number
  colIndex: number
  colName: string
  oldValue: unknown
  newValue: unknown
  rowData?: Record<string, unknown>
  columnName?: string
  columnRole?: 'analyte' | 'sample_attribute'
  columnStructure?: import('@/api').ColumnStructure
  timestamp: string
}

interface ActiveSession {
  session_id: string
  project_id: number
  owner_user_id?: string
  started_at: string
  timer_total_seconds: number
  pages_reviewed: number
  corrections: CellCorrection[]
}

const STORAGE_KEY = 'moria_active_session'

function generateSessionId(): string {
  return `s${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function migrateCorrection(
  correction: Record<string, unknown>,
  legacyHoleId: string | null,
): CellCorrection | null {
  const holeId = optionalString(correction.hole_id) ?? legacyHoleId
  if (!holeId || typeof correction.dataType !== 'string') return null

  const columnRole = correction.columnRole
  return {
    id: optionalString(correction.id),
    operation: optionalString(correction.operation)
      ? (correction.operation as CellCorrection['operation'])
      : 'cell',
    hole_id: holeId,
    extractionId: optionalString(correction.extractionId),
    dataType: correction.dataType,
    rowIndex: typeof correction.rowIndex === 'number' ? correction.rowIndex : 0,
    colIndex: typeof correction.colIndex === 'number' ? correction.colIndex : 0,
    colName: typeof correction.colName === 'string' ? correction.colName : '',
    oldValue: correction.oldValue,
    newValue: correction.newValue,
    rowData:
      correction.rowData && typeof correction.rowData === 'object'
        ? (correction.rowData as Record<string, unknown>)
        : undefined,
    columnName: optionalString(correction.columnName),
    columnRole:
      columnRole === 'analyte' || columnRole === 'sample_attribute' ? columnRole : undefined,
    columnStructure:
      correction.columnStructure && typeof correction.columnStructure === 'object'
        ? (correction.columnStructure as import('@/api').ColumnStructure)
        : undefined,
    timestamp: typeof correction.timestamp === 'string' ? correction.timestamp : '',
  }
}

// Migrates pre-per-project sessions (with top-level hole_id, corrections without hole_id).
// New active sessions span multiple holes; each correction carries its own hole_id.
function migrateLegacySession(raw: unknown): ActiveSession | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  if (typeof obj.session_id !== 'string' || typeof obj.project_id !== 'number') return null

  const legacyHoleId = typeof obj.hole_id === 'string' ? obj.hole_id : null
  const corrections = Array.isArray(obj.corrections)
    ? (obj.corrections as Record<string, unknown>[])
    : []

  const migrated: CellCorrection[] = corrections
    .map((correction) => migrateCorrection(correction, legacyHoleId))
    .filter((c): c is CellCorrection => c !== null)

  return {
    session_id: obj.session_id,
    project_id: obj.project_id,
    owner_user_id: optionalString(obj.owner_user_id),
    started_at: typeof obj.started_at === 'string' ? obj.started_at : new Date().toISOString(),
    timer_total_seconds: typeof obj.timer_total_seconds === 'number' ? obj.timer_total_seconds : 0,
    pages_reviewed: typeof obj.pages_reviewed === 'number' ? obj.pages_reviewed : 0,
    corrections: migrated,
  }
}

function loadFromStorage(): ActiveSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return migrateLegacySession(JSON.parse(raw))
  } catch {
    return null
  }
}

function saveToStorage(session: ActiveSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch {
    /* quota exceeded — best effort */
  }
}

function clearStorage(): void {
  localStorage.removeItem(STORAGE_KEY)
}

let timerTickCount = 0
const TIMER_SAVE_INTERVAL = 30

export const useSessionStore = defineStore('session', () => {
  const current = ref<ActiveSession | null>(loadFromStorage())
  let ownerUserId: string | null = null

  const hasSession = computed(() => current.value !== null)
  const correctionCount = computed(() => current.value?.corrections.length ?? 0)

  const correctionsByType = computed<Record<string, number>>(() => {
    if (!current.value) return {}
    return current.value.corrections.reduce<Record<string, number>>((acc, c) => {
      acc[c.dataType] = (acc[c.dataType] ?? 0) + 1
      return acc
    }, {})
  })

  const correctionsByHole = computed<Record<string, number>>(() => {
    if (!current.value) return {}
    return current.value.corrections.reduce<Record<string, number>>((acc, c) => {
      acc[c.hole_id] = (acc[c.hole_id] ?? 0) + 1
      return acc
    }, {})
  })

  const holesInSession = computed<string[]>(() => Object.keys(correctionsByHole.value).sort())

  function startSession(projectId: number): void {
    const session: ActiveSession = {
      session_id: generateSessionId(),
      project_id: projectId,
      ...(ownerUserId ? { owner_user_id: ownerUserId } : {}),
      started_at: new Date().toISOString(),
      timer_total_seconds: 0,
      pages_reviewed: 0,
      corrections: [],
    }
    current.value = session
    saveToStorage(session)
  }

  function setStorageOwner(userId: string | null): void {
    if (ownerUserId === userId) return
    ownerUserId = userId
    if (!userId || (current.value && current.value.owner_user_id !== userId)) discard()
  }

  function resumeOrStart(projectId: number): void {
    const existing = current.value
    if (existing && existing.project_id === projectId) return
    startSession(projectId)
  }

  function recordCorrection(correction: CellCorrection): void {
    if (!current.value) return
    const operation = correction.operation ?? 'cell'
    const idx = current.value.corrections.findIndex((c) => {
      const existingOperation = c.operation ?? 'cell'
      if (
        c.hole_id !== correction.hole_id ||
        c.extractionId !== correction.extractionId ||
        c.dataType !== correction.dataType ||
        existingOperation !== operation
      ) {
        return false
      }
      if (operation !== 'cell') return !!correction.id && c.id === correction.id
      return c.rowIndex === correction.rowIndex && c.colIndex === correction.colIndex
    })
    if (idx >= 0) {
      const existing = current.value.corrections[idx]
      if (operation !== 'cell') {
        current.value.corrections[idx] = {
          ...existing,
          ...correction,
          operation,
          oldValue: correction.oldValue ?? existing.oldValue,
          newValue: correction.newValue ?? existing.newValue,
        }
        saveToStorage(current.value)
        return
      }
      // Undo path: the user reverted to the cell's original value (the
      // `oldValue` recorded on the very first edit). Drop the correction
      // entirely — there's nothing to commit. Without this, an undo would
      // record a misleading `{old: <intermediate>, new: <original>}` entry.
      if (correction.newValue === existing.oldValue) {
        current.value.corrections.splice(idx, 1)
      } else {
        // Successive edit on the same cell: preserve the original `oldValue`
        // (the value as it appeared in source data, not the most recent
        // intermediate edit), update the new value and timestamp.
        current.value.corrections[idx] = {
          ...existing,
          newValue: correction.newValue,
          timestamp: correction.timestamp,
        }
      }
    } else {
      current.value.corrections.push({ ...correction, operation })
    }
    saveToStorage(current.value)
  }

  function tickTimer(seconds: number): void {
    if (!current.value) return
    current.value.timer_total_seconds += seconds
    timerTickCount++
    if (timerTickCount % TIMER_SAVE_INTERVAL === 0) saveToStorage(current.value)
  }

  function discard(): void {
    current.value = null
    clearStorage()
  }

  return {
    current,
    hasSession,
    correctionCount,
    correctionsByType,
    correctionsByHole,
    holesInSession,
    setStorageOwner,
    startSession,
    resumeOrStart,
    recordCorrection,
    tickTimer,
    discard,
  }
})
